import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import storage from "@/lib/storage";

const AuthCtx = createContext(null);

function _tokenExpiry(token) {
    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.exp ? payload.exp * 1000 : null;
    } catch {
        return null;
    }
}

const REFRESH_THRESHOLD_MS = 24 * 60 * 60 * 1000; // refresh when < 24h left

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => storage.getItem("qc_token"));
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        // Hard timeout so a hung network on cold start never leaves loading=true
        // forever (which shows a blank screen). 8s is generous for a banking app;
        // on timeout we clear the session so the user lands on login cleanly.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        try {
            const r = await api.get("/api/users/me", { signal: controller.signal });
            clearTimeout(timeoutId);
            setUser(r.data);
            // Silently refresh if the token is within 24h of expiry.
            // Only updates storage — avoids re-triggering this effect.
            const stored = storage.getItem("qc_token");
            if (stored) {
                const expiry = _tokenExpiry(stored);
                if (expiry && expiry - Date.now() < REFRESH_THRESHOLD_MS) {
                    try {
                        const ref = await api.post("/api/auth/refresh");
                        storage.setItem("qc_token", ref.data.access_token);
                    } catch {
                        // Refresh failed — current token still valid, continue
                    }
                }
            }
        } catch {
            clearTimeout(timeoutId);
            setUser(null);
            setToken(null);
            storage.removeItem("qc_token");
        }
    }, []);

    useEffect(() => {
        if (token) {
            // Hard ceiling: if fetchMe never settles (e.g. an abort that doesn't fire in
            // some WebViews), don't pin loading=true forever and hang on the loader.
            let settled = false;
            const ceiling = setTimeout(() => { if (!settled) setLoading(false); }, 10000);
            fetchMe().finally(() => { settled = true; clearTimeout(ceiling); setLoading(false); });
        } else {
            setLoading(false);
        }
    }, [token, fetchMe]);

    // Cross-tab sync: react to login/logout in other tabs + session expiry events
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === "qc_token") {
                if (!e.newValue && token) {
                    // logged out in another tab
                    setToken(null);
                    setUser(null);
                } else if (e.newValue && e.newValue !== token) {
                    // logged in elsewhere → adopt new token
                    setToken(e.newValue);
                }
            }
        };
        const onExpired = () => {
            setToken(null);
            setUser(null);
        };
        window.addEventListener("storage", onStorage);
        window.addEventListener("qc_session_expired", onExpired);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("qc_session_expired", onExpired);
        };
    }, [token]);

    const login = async (email, password) => {
        const r = await api.post("/api/auth/login", { email, password });
        storage.setItem("qc_token", r.data.access_token);
        setToken(r.data.access_token);
        setUser(r.data.user);
        return r.data.user;
    };

    const register = async (email, password, name, referralCode) => {
        const r = await api.post("/api/auth/register", { email, password, name, referral_code: referralCode || null });
        storage.setItem("qc_token", r.data.access_token);
        setToken(r.data.access_token);
        setUser(r.data.user);
        return r.data.user;
    };

    const setAuth = (newToken, newUser) => {
        if (newToken) storage.setItem("qc_token", newToken);
        setToken(newToken);
        setUser(newUser);
    };

    const logout = () => {
        storage.removeItem("qc_token");
        storage.removeItem("qc_portfolio");
        setToken(null);
        setUser(null);
    };

    const refresh = fetchMe;

    const updateUser = useCallback((next) => setUser(next), []);

    return (
        <AuthCtx.Provider value={{ user, token, loading, login, register, logout, refresh, setAuth, updateUser }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
